import React from "react";
import { useNavigate } from "react-router-dom";
import Header from "src/components/Header";
import Footer from "src/components/Footer";

function Privacy() {
  const navigate = useNavigate();

  return (
    <div className="relative z-10 h-full w-full flex flex-col items-center justify-start mx-[16px]">
      <div className="w-full items-center justify-center flex px-2 md:px-[15%]">
        <Header isUserLoggedIn={true} onPress={() => navigate("/")} />
      </div>
      <main className="bg-white rounded-3xl shadow-lg p-8 w-full max-w-3xl my-10 text-black h-full overflow-y-scroll">
        <h1 className="text-3xl font-bold mb-6">Privacy Policy</h1>

        <section className="mb-6">
          <h2 className="text-xl font-semibold mb-2">1. Data Controller</h2>
          <p>
            Sparetime Ltd<br />
            Registered in United Kingdom – Company Number: 12881153<br />
            Registered Address: 88 Lowergate, Huddersfield, England, HD3 4EP<br />
            Email: <a href="mailto:info@sparetimetravel.com" className="text-blue-600">info@sparetimetravel.com</a><br />
            Phone: +44 7543 212836
          </p>
          <p className="mt-2">
            If you are located in the EU/UK and wish to contact our Data Protection Officer (DPO) regarding privacy matters, you can reach us at <a href="mailto:info@sparetimetravel.com" className="text-blue-600">info@sparetimetravel.com</a>.
          </p>
        </section>

        <section className="mb-6">
          <h2 className="text-xl font-semibold mb-2">2. Information We Collect</h2>
          <ul className="list-disc list-inside space-y-1">
            <li>Personal Data – name, email address, account information, billing details</li>
            <li>Trip Details – destinations, travel dates, preferences, itinerary notes</li>
            <li>Device & Technical Data – IP address, browser type, operating system, device identifiers</li>
            <li>Usage Data – pages visited, clicks, search queries, timestamps, app interactions</li>
            <li>Location Data – if enabled, approximate location for trip suggestions or alerts</li>
            <li>Cookies & Tracking Data – collected via cookies, analytics, and similar technologies</li>
          </ul>
        </section>

        <section className="mb-6">
          <h2 className="text-xl font-semibold mb-2">3. How We Use Your Information</h2>
          <p>We process your data for the following purposes:</p>
          <ul className="list-disc list-inside space-y-1">
            <li>Service Provision – delivering trip planning, notifications, and itinerary services</li>
            <li>Personalization – tailoring travel recommendations and content</li>
            <li>Communication – sending confirmations, updates, or promotional offers (with consent where required)</li>
            <li>Security & Compliance – protecting against fraud, maintaining platform security, complying with legal obligations</li>
            <li>Analytics & Improvement – analyzing usage trends and improving our services</li>
          </ul>
          <p className="mt-2 font-semibold">Legal Bases for Processing (if you are in the EU/UK):</p>
          <ul className="list-disc list-inside space-y-1">
            <li>Performance of a contract with you</li>
            <li>Compliance with legal obligations</li>
            <li>Legitimate interests (e.g., improving services, fraud prevention)</li>
            <li>Consent (where applicable, e.g., marketing communications)</li>
          </ul>
        </section>

        <section className="mb-6">
          <h2 className="text-xl font-semibold mb-2">4. Sharing Your Information</h2>
          <p>We do not sell your personal data. We may share information with:</p>
          <ul className="list-disc list-inside space-y-1">
            <li>Service Providers – hosting, analytics, payment processors, customer support</li>
            <li>Business Partners & Affiliates – travel providers or affiliates assisting with service delivery</li>
            <li>Legal Authorities – when required by law or to protect our rights</li>
          </ul>
          <p className="mt-2">All third parties are required to implement appropriate safeguards for your data.</p>
        </section>

        <section className="mb-6">
          <h2 className="text-xl font-semibold mb-2">5. Data Retention</h2>
          <ul className="list-disc list-inside space-y-1">
            <li>Account & trip data: retained while your account is active + up to 5 years thereafter</li>
            <li>Logs and analytics: retained for up to 3 years</li>
            <li>Marketing preferences: until you withdraw consent</li>
          </ul>
        </section>

        <section className="mb-6">
          <h2 className="text-xl font-semibold mb-2">6. Cookies & Tracking</h2>
          <p>We use cookies and similar technologies to:</p>
          <ul className="list-disc list-inside space-y-1">
            <li>Improve functionality and user experience</li>
            <li>Analyze trends and app usage</li>
            <li>Provide personalized recommendations and ads (with consent)</li>
          </ul>
          <p className="mt-2">You can manage your cookie preferences in your device or browser settings.</p>
        </section>

        <section className="mb-6">
          <h2 className="text-xl font-semibold mb-2">7. International Transfers</h2>
          <p>Your data may be processed or stored outside your country. When transferring internationally, we ensure appropriate safeguards, such as standard contractual clauses or other legal mechanisms, to protect your data.</p>
        </section>

        <section className="mb-6">
          <h2 className="text-xl font-semibold mb-2">8. Your Rights</h2>
          <p>Depending on your location, you have the right to:</p>
          <ul className="list-disc list-inside space-y-1">
            <li>Access the personal data we hold about you</li>
            <li>Correct or update inaccurate information</li>
            <li>Delete your data (“right to be forgotten”)</li>
            <li>Restrict or object to processing</li>
            <li>Withdraw consent at any time</li>
            <li>Receive your data in a portable format</li>
          </ul>
          <p className="mt-2">To exercise your rights, contact us at <a href="mailto:info@sparetimetravel.com" className="text-blue-600">info@sparetimetravel.com</a> or via postal mail at our registered address. We will respond within the timeframes required by applicable law.</p>
        </section>

        <section className="mb-6">
          <h2 className="text-xl font-semibold mb-2">9. Children</h2>
          <p>Our services are not intended for children under 16. We do not knowingly collect data from minors. If we become aware that we have collected information from a child without parental consent, we will delete it.</p>
        </section>

        <section className="mb-6">
          <h2 className="text-xl font-semibold mb-2">10. Security</h2>
          <p>We implement industry-standard technical and organizational measures to protect your data from unauthorized access, disclosure, alteration, or destruction.</p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-2">11. Changes to This Policy</h2>
          <p>We may update this Privacy Policy from time to time. Significant changes will be communicated via email or app notifications. We recommend reviewing this page periodically. Continued use of our services after updates constitutes acceptance of the revised policy.</p>
          <p className="mt-2 text-sm">Last updated: October 31, 2025</p>
        </section>
      </main>
      <Footer />
    </div>
  );
}

export default Privacy;
